import { ReactNode, FC, useCallback, useRef } from "react";

import { Row, Text } from "@hightouch/htui";

type Props = {
  selected: boolean;
  onClick: () => void;
  icon: ReactNode;
  children: ReactNode;
  end?: ReactNode;
};
export const SelectorRow: FC<Readonly<Props>> = ({
  selected,
  onClick,
  icon,
  children,
  end,
}) => {
  const initialSelected = useRef(selected);

  const scrollToActive = useCallback((node) => {
    if (node && initialSelected.current) {
      node.scrollIntoView({ behavior: "smooth" });
    }
  }, []);

  return (
    <Row
      as="button"
      role="option"
      aria-selected={selected}
      ref={selected ? scrollToActive : undefined}
      alignItems="center"
      bg={selected ? "primary.background" : undefined}
      borderLeft="2px solid" // Indicator line.
      borderLeftColor={selected ? "teal.base" : "transparent"}
      borderBottomWidth="hairline"
      borderBottomStyle="solid"
      borderBottomColor="base.divider"
      cursor="pointer"
      py={2}
      pl={3}
      pr={3}
      gap={2}
      justifyContent="space-between"
      transitionProperty="common"
      transitionDuration="fast"
      transitionTimingFunction="easeOutQuad"
      _last={{ borderBottomColor: "transparent" }}
      _hover={{
        bg: selected ? undefined : "base.background",
      }}
      _focus={{ outline: "none" }}
      _focusVisible={{
        outlineOffset: "-2px",
        outlineWidth: "1px",
        outlineColor: "primary.border",
      }}
      sx={{
        svg: {
          fill: selected ? "teal.base" : undefined,
        },
      }}
      onClick={onClick}
    >
      <Row align="center" gap={1}>
        {icon}
        <Text>{children}</Text>
      </Row>
      {end && <Row>{end}</Row>}
    </Row>
  );
};
